import type { ContactPoint, Patient as FhirPatient } from '@medplum/fhirtypes'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { EXTERNAL_RESOURCE_JSON_SYSTEM } from '#src/codeSystems.ts'
import { elationSandboxIdentifier, extractElationId } from '#src/elation.ts'
import { invariant } from '#src/invariant.ts'
import * as addresses from '#src/models/elation/addresses.ts'
import type { Insurance } from '#src/models/elation/insurances.ts'
import { addFhirRef, metadataSchema } from '#src/models/elation/metadata.ts'
import { homeAddressOrFallback } from '#src/models/patients/patients.ts'
import type { ExtendedPractitioner } from '#src/models/practitioner.ts'
import { getReferenceString } from '#src/references.ts'
import { build, now, officialNameOrFallback, recordInvert } from '#src/utils.ts'

export const phoneSchema = z.object({
  phone: z.string().max(20),
  phone_type: z.enum([
    'Mobile',
    'Home',
    'Main',
    'Work',
    'Night',
    'Fax',
    'Other',
  ]),
  created_date: z.string(), // ISO8601 datetime string
  deleted_date: z.string().nullable(),
})

export type Phone = z.infer<typeof phoneSchema>

export const emailSchema = z.object({
  email: z.string().max(75),
  created_date: z.string(), // ISO8601 datetime string
  deleted_date: z.string().nullable(),
})

export const insuranceSchema = z.object({
  id: z.number().optional(),
  insurance_company: z.number().optional().nullable(),
  insurance_plan: z.number().optional().nullable(),
  rank: z.enum(['primary', 'secondary', 'tertiary']).optional().nullable(),
  carrier: z.string().max(200).optional().nullable(),
  member_id: z.string().max(50).optional().nullable(),
  group_id: z.string().max(50).optional().nullable(),
  plan: z.string().max(200).optional().nullable(),
  phone: z.string().max(20).optional().nullable(),
  extension: z.string().max(6).optional().nullable(),
  address: z.string().max(200).optional().nullable(),
  suite: z.string().max(35).optional().nullable(),
  city: z.string().max(50).optional().nullable(),
  state: z.string().max(2).optional().nullable(),
  zip: z.string().max(9).optional().nullable(),
  copay: z.number().nullable(),
  deductible: z.number().nullable(),
  payment_program: z
    .enum([
      'Medicare Part B',
      'Medicare Advantage',
      'Medicaid',
      'Commercial - HMSA',
      'Commercial - SFHP',
      'Commercial - Other',
      "Worker's Compensation",
    ])
    .optional()
    .nullable(),
  insured_person_first_name: z.string().max(200).optional().nullable(),
  insured_person_last_name: z.string().max(200).optional().nullable(),
  insured_person_address: z.string().max(200).optional().nullable(),
  insured_person_city: z.string().max(50).optional().nullable(),
  insured_person_state: z.string().max(2).optional().nullable(),
  insured_person_zip: z.string().max(9).optional().nullable(),
  insured_person_id: z.string().max(50).optional().nullable(),
  insured_person_dob: z.string().max(50).optional().nullable(), // Assuming string type for date to match the format "MM/DD/YYYY"
  insured_person_gender: z.enum(['M', 'F', 'N']).optional().nullable(),
  insured_person_ssn: z.string().max(9).optional().nullable(),
  relationship_to_insured: z.string().max(20).optional().nullable(),
  created_date: z.string().optional().nullable(), // ISO8601 datetime string
  deleted_date: z.string().nullable(),
  start_date: z.string().nullable(), // Assuming nullable string to represent date in "YYYY-MM-DD" format
  end_date: z.string().nullable(),
})

export const patientStatusStatusOptions = {
  Active: 'active',
  Deceased: 'deceased',
  Inactive: 'inactive',
  Prospect: 'prospect',
} as const

export const patientStatusSchema = z.object({
  deceased_date: z.string().optional().nullable(), // Assuming optional date string in "YYYY-MM-DD" format
  inactive_reason: z
    .enum([
      'other',
      'left_bad_terms',
      'left_good_terms',
      'practice_ended',
      'unknown',
    ])
    .optional()
    .nullable(),
  last_status_change: z.string().optional().nullable().readonly(), // ISO8601 datetime string
  notes: z.string().optional().nullable(),
  status: z.nativeEnum(patientStatusStatusOptions),
})

export type PatientStatus = z.infer<typeof patientStatusSchema>

export const emergencyContactSchema = z.object({
  first_name: z.string().max(70),
  last_name: z.string().max(70),
  relationship: z.enum([
    'Caregiver',
    'Child',
    'Friend',
    'Grandparent',
    'Guardian',
    'Parent',
    'Sibling',
    'Spouse',
    'Other',
  ]),
  phone: z.string().max(20),
  address_line1: z.string().max(200),
  address_line2: z.string().max(35).optional().nullable(),
  city: z.string().max(50),
  state: z.string().max(2),
  zip: z.string().max(9),
})

export const previousNameSchema = z.object({
  first_name: z.string().max(70),
  middle_name: z.string().max(50).optional().nullable(),
  last_name: z.string().max(70),
  suffix: z.string().max(20).optional().nullable(),
})

export const employerSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string(),
})

export const consentSchema = z.object({
  consented: z.boolean(),
  last_modified_date: z.string(), // ISO8601 datetime string
  application: z.string().max(255),
})

export const patientRaceEnum = [
  'No race specified',
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Native Hawaiian or Other Pacific Islander',
  'White',
  'Declined to specify',
] as const

export const patientEthnicityEnum = [
  'No ethnicity specified',
  'Hispanic or Latino',
  'Not Hispanic or Latino',
  'Declined to specify',
] as const

export const patientSchema = z.object({
  id: z.number().int().optional().nullable(), // Marked as read-only
  first_name: z.string().max(70),
  middle_name: z.string().max(50).optional().nullable(),
  last_name: z.string().max(70),
  actual_name: z.string().max(150).optional().nullable(),
  gender_identity: z
    .enum([
      'unknown',
      'man',
      'woman',
      'transgender_man',
      'transgender_woman',
      'nonbinary',
      'option_not_listed',
      'prefer_not_to_say',
      'two_spirit',
    ])
    .optional()
    .nullable(),
  legal_gender_marker: z.enum(['M', 'F', 'X', 'U']).optional().nullable(),
  pronouns: z
    .enum(['he_him_his', 'she_her_hers', 'they_them_theirs', 'not_listed'])
    .optional()
    .nullable(),
  sex: z.enum(['Male', 'Female', 'Other', 'Unknown']),
  sexual_orientation: z
    .enum([
      'unknown',
      'straight',
      'gay',
      'bisexual',
      'option_not_listed',
      'prefer_not_to_say',
      'lesbian',
      'queer',
      'asexual',
    ])
    .optional()
    .nullable(),
  primary_physician: z.number(),
  caregiver_practice: z.number(), // Marked as read-only
  dob: z.string(), // Date string in "YYYY-MM-DD" format
  ssn: z.string().max(9).optional().nullable(),
  race: z.enum(patientRaceEnum),
  preferred_language: z.string().nullable(), // Assuming dynamic language resource validation is handled elsewhere
  ethnicity: z.enum(patientEthnicityEnum),
  notes: z.string().max(500).optional().nullable(),
  vip: z.boolean().optional().nullable(), // Feature must be enabled for practice
  address: addresses.addressSchema.optional().nullable(),
  phones: z.array(phoneSchema),
  emails: z.array(emailSchema), // Assuming validation for empty deleted_date is handled elsewhere
  guarantor: z
    .object({
      id: z.number(),
      address: z.string(),
      city: z.string(),
      state: z.string(),
      zip: z.string().max(9),
      phone: z.string().max(20),
      relationship: z.enum(['Spouse', 'Child', 'Other']),
      first_name: z.string(),
      last_name: z.string(),
      middle_name: z.string().nullable(),
    })
    .nullable(),
  insurances: z.array(insuranceSchema).max(2),
  deleted_insurances: z.array(insuranceSchema).max(5), // Assuming similar structure for active and deleted insurances
  tags: z.array(z.string().max(100)).max(10),
  patient_status: patientStatusSchema,
  verified: z.boolean().optional().nullable(),
  preference: z
    .object({
      preferred_pharmacy_1: z.string().nullable(), // Assuming NCPDP ID as string; validation handled elsewhere
      preferred_pharmacy_2: z.string().nullable(),
    })
    .optional()
    .nullable(),
  emergency_contact: emergencyContactSchema.optional().nullable(),
  primary_care_provider: z.number().optional().nullable(), // Marked as read-only
  primary_care_provider_npi: z.string().max(10).optional().nullable(),
  previous_name: previousNameSchema.optional().nullable(),
  master_patient: z.number().nullable().optional().nullable(), // Assuming nullable for optional linkage to a master patient record
  employer: employerSchema.optional().nullable(), // Enterprise use only
  consents: z.array(consentSchema).optional().nullable(), // Marked as read-only
  metadata: metadataSchema.nullable().optional(),
  created_date: z.string().optional().nullable(), // ISO8601 datetime string, marked as read-only
  deleted_date: z.string().nullable().optional().nullable(), // ISO8601 datetime string, marked as read-only
  merged_into_chart: z.number().int().nullable().optional().nullable(), // Marked as read-only, assuming nullable for potential merges
})

export type Patient = z.infer<typeof patientSchema>

const fromFhirContactPointUseMap: Record<
  NonNullable<ContactPoint['use']>,
  Phone['phone_type']
> = {
  home: 'Home',
  work: 'Work',
  mobile: 'Mobile',
  old: 'Other',
  temp: 'Other',
}
const toFhirContactPointUseMap = recordInvert(fromFhirContactPointUseMap)

const fromFhirGenderMap: Record<
  NonNullable<FhirPatient['gender']>,
  NonNullable<Patient['sex']>
> = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  unknown: 'Unknown',
}
const toFhirGenderMap = recordInvert(fromFhirGenderMap)

export const fromFhirForCreate = ({
  patient,
  mrn,
  pcp,
  elationPracticeId,
  elationPreferredPharmacyId,
  elationInsuranceCompany,
}: {
  patient: FhirPatient
  mrn: string
  pcp: ExtendedPractitioner
  elationPracticeId: number
  elationPreferredPharmacyId?: string
  elationInsuranceCompany?: Insurance
}): Patient => {
  const dob = patient.birthDate
  invariant(dob, `${getReferenceString(patient)} must have a birth date`)

  const pcpElationId = pcp.elationId
  invariant(pcpElationId, `${pcp.reference.reference} must have an elation id`)

  const patientName = officialNameOrFallback(patient)
  invariant(
    patientName?.given != null && patientName?.family != null,
    `${getReferenceString(patient)} must have a name`
  )

  const patientStatus = match<
    FhirPatient,
    Pick<PatientStatus, 'status' | 'deceased_date'>
  >(patient)
    .when(
      (p) => p.deceasedBoolean ?? p.deceasedDateTime,
      (p) => ({
        status: 'deceased',
        deceased_date: p.deceasedDateTime,
      })
    )
    .when(
      (p) => p.active === false,
      () => ({ status: 'inactive' })
    )
    .otherwise(() => ({ status: 'active' }))

  const result: Patient = {
    id: extractElationId(patient),
    first_name: patientName.given.join(' '),
    last_name: patientName.family,
    sex: fromFhirGenderMap[patient.gender ?? 'unknown'],
    dob,

    primary_physician: pcpElationId,
    primary_care_provider: pcpElationId,
    caregiver_practice: elationPracticeId,

    preferred_language: null,
    preference: {
      preferred_pharmacy_1: elationPreferredPharmacyId
        ? elationPreferredPharmacyId
        : null,
      preferred_pharmacy_2: null,
    },
    race: 'No race specified',
    ethnicity: 'No ethnicity specified',
    phones: (patient.telecom ?? [])
      .filter(
        (
          t
        ): t is ContactPoint & {
          system: 'phone'
          value: string
        } => !!t.value && t.system === 'phone'
      )
      .map((t) => ({
        phone: t.value,
        phone_type: t.use ? fromFhirContactPointUseMap[t.use] : 'Other',
        created_date: now(),
        deleted_date: null,
      })),
    emails: (patient.telecom ?? [])
      .filter(
        (
          t
        ): t is ContactPoint & {
          system: 'email'
          value: string
        } => !!t.value && t.system === 'email'
      )
      .map((t) => ({
        email: t.value,
        created_date: now(),
        deleted_date: null,
      })),
    address: patient.address?.[0] && addresses.fromFhir(patient.address[0]),
    guarantor: null,
    // @ts-expect-error not all fields are required on create so short of adding a create and read schema, this should be fine for now and was manually tested to work
    insurances: elationInsuranceCompany
      ? [
          {
            insurance_company: elationInsuranceCompany.id,
            member_id: mrn,
            rank: 'primary',
            carrier: elationInsuranceCompany.carrier,
            phone: elationInsuranceCompany.phone,
            address: elationInsuranceCompany.address,
            city: elationInsuranceCompany.city,
            state: elationInsuranceCompany.state,
            zip: elationInsuranceCompany.zip,
          },
        ]
      : [],
    deleted_insurances: [],
    tags: [],
    patient_status: patientStatus,
  }

  return addFhirRef(result, patient)
}

export const fromFhirForUpdate = ({
  patient,
  elationPracticeId,
  elationPreferredPharmacyId,
}: {
  patient: FhirPatient
  elationPracticeId: number
  elationPreferredPharmacyId?: string
}): Partial<Patient> => {
  const dob = patient.birthDate
  invariant(dob, `${getReferenceString(patient)} must have a birth date`)

  const patientName = officialNameOrFallback(patient)
  invariant(
    patientName?.given != null && patientName?.family != null,
    `${getReferenceString(patient)} must have a name`
  )

  const patientStatus = match<FhirPatient, PatientStatus>(patient)
    .when(
      (p) => p.deceasedBoolean ?? p.deceasedDateTime,
      (p) => ({
        status: 'deceased',
        deceased_date: p.deceasedDateTime,
      })
    )
    .when(
      (p) => p.active === false,
      () => ({ status: 'inactive', inactive_reason: 'unknown' })
    )
    .otherwise(() => ({ status: 'active' }))

  const homeAddr = homeAddressOrFallback(patient)

  const result: Partial<Patient> = {
    id: extractElationId(patient),
    first_name: patientName.given.join(' '),
    last_name: patientName.family,
    sex: fromFhirGenderMap[patient.gender ?? 'unknown'],
    dob,

    caregiver_practice: elationPracticeId,

    preference: {
      preferred_pharmacy_1: elationPreferredPharmacyId
        ? elationPreferredPharmacyId
        : null,
      preferred_pharmacy_2: null,
    },
    phones: (patient.telecom ?? [])
      .filter(
        (
          t
        ): t is ContactPoint & {
          system: 'phone'
          value: string
        } => !!t.value && t.system === 'phone'
      )
      .map((t) => ({
        phone: t.value,
        phone_type: t.use ? fromFhirContactPointUseMap[t.use] : 'Other',
        created_date: now(),
        deleted_date: null,
      })),
    emails: (patient.telecom ?? [])
      .filter(
        (
          t
        ): t is ContactPoint & {
          system: 'email'
          value: string
        } => !!t.value && t.system === 'email'
      )
      .map((t) => ({
        email: t.value,
        created_date: now(),
        deleted_date: null,
      })),
    address: homeAddr ? addresses.fromFhir(homeAddr) : undefined,
    patient_status: patientStatus,
  }

  return addFhirRef(result, patient)
}

export const toFhir = (patient: Patient): FhirPatient =>
  build('Patient', {
    identifier: [elationSandboxIdentifier(patient.id?.toString())],
    name: [
      {
        use: 'official',
        given: [patient.first_name],
        family: patient.last_name,
      },
    ],
    gender: toFhirGenderMap[patient.sex],
    address: patient.address ? [addresses.toFhir(patient.address, 'home')] : [],
    birthDate: patient.dob,
    // Emit only `true` so a re-sync merge never clears an Orbit-set deceased or active flag.
    deceasedBoolean:
      patient.patient_status.status === 'deceased' ? true : undefined,
    deceasedDateTime: patient.patient_status.deceased_date ?? undefined,
    active:
      patient.patient_status.status === 'prospect' ||
      patient.patient_status.status === 'active'
        ? true
        : undefined,
    telecom: [
      ...patient.phones.map(
        (p) =>
          ({
            system: 'phone',
            value: p.phone,
            use: toFhirContactPointUseMap[p.phone_type],
          }) satisfies ContactPoint
      ),
      ...patient.emails.map(
        (e) =>
          ({
            system: 'email',
            value: e.email,
          }) satisfies ContactPoint
      ),
    ],
    extension: [
      {
        url: EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(patient),
      },
    ],
  })
